import React, { useMemo, useState } from 'react';
import { ImageUpload } from '../components/image-upload';
import { Api } from '../core/api';
import { injectTSDI } from '../core/tsdi';
import { CdnObjectDto } from '@api/FitnessActivityClient';
import { getCdnImage } from '../components/utils/cdn-image';

export interface BadgeImageUploadProps {
    label?: string;
    helperText?: string;
    /**
     * Pre-populate preview with an existing CDN image.
     */
    initialImage?: CdnObjectDto | null;
    /**
     * Called after a successful upload with the CDN object returned by the API.
     */
    onUploaded?: (image: CdnObjectDto) => void;
    /**
     * Called when the user wants to remove/clear the image.
     */
    onRemoved?: () => void;
    maxSizeMB?: number;
}

export const BadgeImageUpload: React.FC<BadgeImageUploadProps> = ({
    label = 'Badge Image',
    helperText = 'Drag and drop a badge image here, or click to select',
    initialImage,
    onUploaded,
    onRemoved,
    maxSizeMB = 5
}) => {
    const { fitnessActivityClient } = injectTSDI(Api);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const [previewUrl, setPreviewUrl] = useState<string | undefined>();

    const resolvedPreview = useMemo(() => {
        if (previewUrl) {
            return previewUrl;
        }
        if (initialImage?.bucket && initialImage?.key) {
            return getCdnImage(initialImage.bucket, initialImage.key, {
                resize: { width: 400 }
            });
        }
        return undefined;
    }, [initialImage, previewUrl]);

    const handleUpload = async (file: File) => {
        setLoading(true);
        setError(undefined);

        try {
            const response =
                await fitnessActivityClient.admin.badgeImageAdminControllerUploadBadgeImage(
                    { file }
                );

            onUploaded?.(response.data);
            setPreviewUrl(URL.createObjectURL(file));
        } catch (err: any) {
            console.error('Badge image upload failed:', err);
            setError(
                err?.response?.data?.message ||
                    err?.message ||
                    'Failed to upload image. Please try again.'
            );
        } finally {
            setLoading(false);
        }
    };

    const handleRemove = () => {
        setPreviewUrl(undefined);
        onRemoved?.();
    };

    return (
        <ImageUpload
            onUpload={handleUpload}
            onRemove={resolvedPreview ? handleRemove : undefined}
            loading={loading}
            error={error}
            previewUrl={resolvedPreview}
            label={label}
            helperText={helperText}
            maxSizeMB={maxSizeMB}
        />
    );
};

export default BadgeImageUpload;
